import type { Attribute, FIELD, FormConfig } from "@/types/form-types"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { API_URL, IS_AMS_DASHBOARD, SAARC_COUNTRIES } from "./constants"
import { parse, isValid } from "date-fns"
import { PaymentPlan } from "./types"
import type z from "zod"
import { apiAMS } from "./api-ams"
import { api } from "./api"

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export function joinUrl(base: string, path: string): string {
	// Ensure base ends with a single slash
	const normalizedBase = base.replace(/\/+$/, "") + "/"

	// Remove any leading slashes from the path
	const normalizedPath = path.replace(/^\/+/, "")

	return normalizedBase + normalizedPath
}

export function getFormatMediaURL(path: string): string {
	if (!path) return ""
	// if (!isUrl(API_URL)) {
	const url = joinUrl(API_URL, "/media")
	// return url.concat("?path=").concat(path)
	// // }
	// const url = new URL(joinUrl(API_URL, "/media"))
	// url.searchParams.set("path", path)
	// return url.toString()
	const params = new URLSearchParams({ path: path })

	return `${url}?${params.toString()}`
}

export function truncatePrefixSlash(str: string): string {
	return str.startsWith("/") ? str.slice(1) : str
}

export function getHostThemeMode(): "light" | "dark" {
	// Check HTML class or data-theme attr
	if (
		document.documentElement.classList.contains("dark") ||
		document.documentElement.getAttribute("data-theme") === "dark"
	) {
		return "dark"
	}
	return "light"
}

export function watchHostThemeMode(onChange: (mode: "light" | "dark") => void) {
	const observer = new MutationObserver(() => {
		const mode = getHostThemeMode()
		onChange(mode)
	})

	observer.observe(document.documentElement, {
		attributes: true,
		attributeFilter: ["class", "data-theme"],
	})
}

export function parseAttributes(
	attributes: Attribute[],
): Record<string, string | number | boolean | any[] | Record<string, any>> {
	const parsed: Record<
		string,
		string | number | boolean | any[] | Record<string, any>
	> = {}

	attributes.forEach(attr => {
		let parsedValue:
			| string
			| number
			| boolean
			| any[]
			| Record<string, any> = attr.value

		switch (attr.data_type) {
			case "NUMBER":
				parsedValue = Number(attr.value)
				break

			case "BOOLEAN":
				parsedValue =
					attr.value === true ||
					attr.value === "true" ||
					attr.value === "TRUE"
				break

			case "ARRAY":
				try {
					parsedValue = Array.isArray(attr.value)
						? attr.value
						: JSON.parse(attr.value)
				} catch {
					parsedValue = []
				}
				break

			case "OBJECT":
				try {
					parsedValue =
						typeof attr.value === "object"
							? attr.value
							: JSON.parse(attr.value)
				} catch {
					parsedValue = {}
				}
				break
			case "DATE":
				parsedValue = new Date(attr.value)
				break

			case "STRING":
			default:
				parsedValue = String(attr.value)
				break
		}

		parsed[attr.name] = parsedValue
	})

	return parsed
}

// export function normalizeApiResponse(data: Record<string, string>) {
// 	const result: Record<string, unknown> = {}

// 	for (const [key, value] of Object.entries(data)) {
// 		try {
// 			result[key] = JSON.parse(value) // stringified JSON → real type
// 		} catch {
// 			result[key] = value // fallback if not JSON
// 		}
// 	}

// 	return result
// }

export function normalizeApiResponse(
	data: Record<string, any>,
	schema: z.ZodObject<any>,
): Record<string, any> {
	const normalizedData: Record<string, any> = {}

	for (const key in data) {
		if (
			Object.prototype.hasOwnProperty.call(data, key) &&
			schema.shape[key]
		) {
			let value = ""
			try {
				value = JSON.parse(data[key])
			} catch {
				value = data[key]
			}
			// const fieldSchema = schema.shape[key]

			// // Check if the value is an empty string.
			// if (value === "") {
			// 	// Now, we use the schema to determine the field type.
			// 	switch (fieldSchema._def.typeName) {
			// 		case "ZodString":
			// 		case "ZodNumber":
			// 		case "ZodEnum":
			// 			// For string and number fields, an empty string should be undefined.
			// 			normalizedData[key] = undefined
			// 			break
			// 		case "ZodArray":
			// 			// For array fields, an empty string should be an empty array.
			// 			normalizedData[key] = []
			// 			break
			// 		case "ZodObject":
			// 			normalizedData[key] = {}
			// 			break
			// 		default:
			// 			// For all other types, we keep the empty string as is.
			// 			normalizedData[key] = value
			// 			break
			// 	}
			// } else {
			// If the value is not an empty string, we use it as is.
			normalizedData[key] = value
			// }
		}
	}

	return normalizedData
}

export function removeTrailingChars(str: string, char: string): string {
	let result = str
	while (result.endsWith(char)) {
		result = result.slice(0, -1)
	}
	return result
}

export function getFieldById(
	form?: FormConfig,
	fieldId?: string,
): FIELD | undefined {
	if (!fieldId || !form) return undefined

	for (const stage of form.stages) {
		for (const section of stage.sections) {
			for (const row of section.rows) {
				for (const column of row.columns) {
					for (const field of column.fields) {
						if (field.field_id === fieldId) {
							return field
						}
					}
				}
			}
		}
	}
	return undefined
}

export function getNationalityByCoutry(country: string): string {
	// if (!country) return

	if (country === "India") {
		return "FDO_NAT_IND"
	} else if (SAARC_COUNTRIES.includes(country)) {
		return "FDO_NAT_SAA"
	} else return "FDO_NAT_FOR"
}

export function toDateSafe(
	value: string | Date | null | undefined,
	formatHint: string = "dd/MM/yyyy",
): Date | null {
	if (!value) return null

	if (value instanceof Date) {
		return isValid(value) ? value : null
	}

	// Try native Date parse (works for ISO strings)
	const native = new Date(value)
	if (isValid(native)) return native

	// Try custom format (for non-ISO strings like dd/MM/yyyy)
	const parsed = parse(value, formatHint, new Date())
	return isValid(parsed) ? parsed : null
}

export function getPlanType(frequency: string) {
	if (frequency === "PER_YEAR") {
		return PaymentPlan.YEAR
	} else if (frequency === "PER_SEMESTER") {
		return PaymentPlan.SEMESTER
	} else if (frequency === "ONE_TIME") {
		return PaymentPlan.FULL
	} else {
		return frequency
	}
}

export function getStudentInfo(
	formConfig: FormConfig,
	alias: string,
): FIELD | undefined {
	return formConfig?.stages
		?.flatMap(stage => stage.sections)
		?.flatMap(section => section.rows)
		?.flatMap(row => row.columns)
		?.flatMap(column => column.fields)
		?.find(field =>
			field.attributes?.some(
				attr => attr.name === "alias" && attr.value?.startsWith(alias),
			),
		)
}

export async function downloadFileFromURl(
	url: string,
	filename: string,
	type: string,
	dummyFile: string = "",
) {
	let fileData
	let response

	try {
		if (IS_AMS_DASHBOARD) {
			response = await apiAMS.get(url, { responseType: "blob" })
		} else {
			response = await api.get(url, { responseType: "blob" })
		}
	} catch (error: any) {
		if (error.response.status !== 200) {
			if (error.response.status === 403) throw new Error("Unauthorized")
			else throw new Error("Failed to download file")
		}
	}

	fileData = response?.data
	if ((!fileData || fileData.size === 0) && dummyFile) {
		fileData = new Blob([dummyFile], { type: type })
	}
	const blob =
		fileData instanceof Blob
			? fileData
			: new Blob([fileData], { type: type })
	const linkUrl = window.URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = linkUrl
	link.download = filename
	document.body.appendChild(link)
	link.click()
	link.remove()
	window.URL.revokeObjectURL(linkUrl)
}
